import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { getActions } from '../services/data';
import type { CommunityAction } from '../types';
import community from '../assets/community-action.webp';
import activePark from '../assets/active-park.webp';
import workshop from '../assets/climate-workshop.webp';

const actionImages: Record<string, string> = {
  'Clean Run': activePark,
  Penghijauan: community,
  Edukasi: workshop,
};

function loadJoined(): string[] {
  try {
    const value = localStorage.getItem('jc-joined-actions');
    return value ? JSON.parse(value) as string[] : [];
  } catch {
    return [];
  }
}

function formatActionDate(dateValue: string): { day: string; month: string; weekday: string } {
  const date = new Date(`${dateValue}T00:00:00`);
  return {
    day: new Intl.DateTimeFormat('id-ID', { day: '2-digit' }).format(date),
    month: new Intl.DateTimeFormat('id-ID', { month: 'short' }).format(date),
    weekday: new Intl.DateTimeFormat('id-ID', { weekday: 'long' }).format(date),
  };
}

function downloadCalendar(action: CommunityAction) {
  const normalizedDate = action.date.replaceAll('-', '');
  const normalizedTime = action.time.replace('.', '').padEnd(4, '0');
  const start = `${normalizedDate}T${normalizedTime}00`;
  const endHour = String((Number(normalizedTime.slice(0, 2)) + 2) % 24).padStart(2, '0');
  const end = `${normalizedDate}T${endHour}${normalizedTime.slice(2)}00`;
  const body = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//JakComfort//Community Action//ID',
    'BEGIN:VEVENT',
    `UID:${action.id}@jakcomfort.local`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${action.title}`,
    `LOCATION:${action.location}`,
    `DESCRIPTION:${action.description} Penyelenggara: ${action.organizer}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
  const blob = new Blob([body], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${action.id}.ics`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function ActionsPage() {
  const [actions, setActions] = useState<CommunityAction[]>([]);
  const [selected, setSelected] = useState<CommunityAction | null>(null);
  const [category, setCategory] = useState('Semua');
  const [joined, setJoined] = useState<string[]>(loadJoined);
  const [showProposal, setShowProposal] = useState(false);
  const [proposalSent, setProposalSent] = useState(false);
  const [showJoinedOnly, setShowJoinedOnly] = useState(false);

  useEffect(() => {
    void getActions().then((data) => {
      setActions(data);
      setSelected(data[0] ?? null);
    });
  }, []);

  useEffect(() => {
    localStorage.setItem('jc-joined-actions', JSON.stringify(joined));
  }, [joined]);

  const categories = useMemo(() => ['Semua', ...Array.from(new Set(actions.map((action) => action.category)))], [actions]);
  const visibleActions = useMemo(() => actions.filter((action) => (category === 'Semua' || action.category === category) && (!showJoinedOnly || joined.includes(action.id))), [actions, category, joined, showJoinedOnly]);
  const selectedDate = selected ? formatActionDate(selected.date) : null;

  function toggleJoin(id: string) {
    setJoined((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  }

  function submitProposal(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setProposalSent(true);
  }

  function exploreActions() {
    document.getElementById('action-library')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <div className="app-page actions-page actions-page--interactive">
      <header className="app-page__header">
        <div>
          <span className="eyebrow">Citizen Climate Action</span>
          <h1>Temukan kegiatan, simpan agenda, dan mulai aksi kecil bersama warga Jakarta.</h1>
        </div>
        <button className="button button--primary button--small" type="button" onClick={() => { setShowProposal(true); setProposalSent(false); }}>Ajukan kegiatan</button>
      </header>

      <section className="actions-overview">
        <article className="actions-intro-card">
          <img src={community} alt="Komunitas JakComfort melakukan aksi penghijauan" />
          <div className="actions-intro-card__overlay" />
          <div className="actions-intro-card__content">
            <span className="eyebrow eyebrow--light">Aksi warga bulan ini</span>
            <h2>Pilih kegiatan yang dekat, simpan agendanya, lalu bergerak bersama.</h2>
            <p>Dari clean run hingga penghijauan, semua kegiatan dapat dipilih berdasarkan minat dan disimpan ke kalender.</p>
            <div className="actions-intro-card__buttons">
              <button className="button button--light button--small" type="button" onClick={exploreActions}>Jelajahi kegiatan</button>
              <button className={`button button--small ${showJoinedOnly ? 'button--joined' : 'learning-button--glass'}`} type="button" onClick={() => setShowJoinedOnly((value) => !value)}>
                {showJoinedOnly ? 'Tampilkan semua' : `Agenda saya (${joined.length})`}
              </button>
            </div>
          </div>
        </article>

        <div className="actions-summary-grid">
          <button type="button" className="actions-summary-card" onClick={exploreActions}><span>✦</span><div><strong>{actions.length || 3}</strong><small>Kegiatan tersedia</small></div></button>
          <button type="button" className="actions-summary-card" onClick={() => setShowJoinedOnly((value) => !value)}><span>✓</span><div><strong>{joined.length}</strong><small>Agenda tersimpan</small></div></button>
          <button type="button" className="actions-summary-card" onClick={() => { setShowProposal(true); setProposalSent(false); }}><span>＋</span><div><strong>Ajukan</strong><small>Kegiatan komunitas</small></div></button>
        </div>
      </section>

      <section className="action-toolbar">
        <div className="filter-chips" aria-label="Filter kegiatan">
          {categories.map((item) => (
            <button type="button" className={category === item ? 'is-active' : ''} onClick={() => setCategory(item)} key={item}>{item}</button>
          ))}
        </div>
        <div className="action-toolbar__note"><span>●</span> Data kegiatan pada MVP disimpan sebagai JSON dan interaksi pengguna tersimpan lokal di perangkat.</div>
      </section>

      <div className="actions-layout">
        <section id="action-library" className="action-library">
          <div className="content-section__heading">
            <div><span className="eyebrow">Kegiatan mendatang</span><h2>Pilih aksi yang dekat dengan minatmu</h2></div>
            <span className="learning-count">{visibleActions.length} kegiatan</span>
          </div>

          <div className="action-card-grid action-card-grid--interactive">
            {visibleActions.map((action) => {
              const date = formatActionDate(action.date);
              const isJoined = joined.includes(action.id);
              return (
                <article className={`action-card action-card--rich ${selected?.id === action.id ? 'is-selected' : ''}`} key={action.id}>
                  <button className="action-card__select" type="button" onClick={() => setSelected(action)}>
                    <div className="action-card__image">
                      <img src={actionImages[action.category] ?? community} alt="" />
                      <span className="action-card__category">{action.category}</span>
                    </div>
                    <div className="action-card__content">
                      <div className="action-card__date action-card__date--horizontal"><strong>{date.day}</strong><span>{date.month}</span><small>{date.weekday}</small></div>
                      <h3>{action.title}</h3>
                      <p>{action.description}</p>
                      <div className="action-card__meta">
                        <span>⌖ {action.location}</span>
                        <span>◷ {action.time}</span>
                      </div>
                      <strong className="action-card__organizer">oleh {action.organizer}</strong>
                    </div>
                  </button>
                  <div className="action-card__buttons">
                    <button type="button" className="button button--ghost button--small" onClick={() => setSelected(action)}>Lihat detail</button>
                    <button type="button" className={`button button--small ${isJoined ? 'button--joined' : 'button--primary'}`} onClick={() => toggleJoin(action.id)}>{isJoined ? '✓ Akan ikut' : 'Ikut kegiatan'}</button>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <aside className="action-detail-panel">
          {selected && selectedDate ? (
            <>
              <div className="action-detail-panel__image">
                <img src={actionImages[selected.category] ?? community} alt="" />
                <span>{selected.category}</span>
              </div>
              <div className="action-detail-panel__date">
                <strong>{selectedDate.day}</strong>
                <div><span>{selectedDate.month}</span><small>{selectedDate.weekday}</small></div>
              </div>
              <h2>{selected.title}</h2>
              <p>{selected.description}</p>

              <div className="action-detail-facts">
                <div><span>◷</span><strong>{selected.time}</strong><small>Waktu mulai</small></div>
                <div><span>⌖</span><strong>{selected.location}</strong><small>Lokasi kegiatan</small></div>
                <div><span>◎</span><strong>{selected.organizer}</strong><small>Penyelenggara</small></div>
              </div>

              <section className="action-impact-box">
                <span className="eyebrow">Dampak yang dituju</span>
                <ul>
                  <li>Meningkatkan partisipasi dan koneksi antarkomunitas.</li>
                  <li>Mengubah edukasi lingkungan menjadi pengalaman nyata.</li>
                  <li>Mendokumentasikan aksi untuk komunikasi dan evaluasi dampak.</li>
                </ul>
              </section>

              <div className="action-detail-panel__buttons">
                <button type="button" className={`button button--full ${joined.includes(selected.id) ? 'button--joined' : 'button--primary'}`} onClick={() => toggleJoin(selected.id)}>{joined.includes(selected.id) ? '✓ Tersimpan sebagai peserta' : 'Saya ingin ikut'}</button>
                <button type="button" className="button button--ghost button--full" onClick={() => downloadCalendar(selected)}>Tambahkan ke kalender</button>
              </div>
            </>
          ) : <p>Memuat detail kegiatan...</p>}
        </aside>
      </div>

      <section className="challenge-section challenge-section--interactive">
        <div className="challenge-section__heading">
          <div><span className="eyebrow eyebrow--light">Tantangan lokal</span><h2>Tiga langkah sederhana minggu ini</h2></div>
          <span>{joined.length > 0 ? `${joined.length} agenda aksi sudah kamu simpan` : 'Mulai dari satu langkah kecil'}</span>
        </div>
        <div className="challenge-grid">
          <article><span>1</span><strong>Bawa botol sendiri</strong><p>Kurangi kemasan sekali pakai saat beraktivitas.</p><label><input type="checkbox" /> Tandai dilakukan</label></article>
          <article><span>2</span><strong>Kunjungi ruang hijau</strong><p>Kenali manfaat taman dan ruang publik di sekitar.</p><label><input type="checkbox" /> Tandai dilakukan</label></article>
          <article><span>3</span><strong>Ajak satu orang</strong><p>Bagikan insight atau ikut kegiatan bersama teman.</p><label><input type="checkbox" /> Tandai dilakukan</label></article>
        </div>
      </section>

      {showProposal && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setShowProposal(false)}>
          <section className="action-proposal-modal" role="dialog" aria-modal="true" aria-labelledby="proposal-title" onMouseDown={(event) => event.stopPropagation()}>
            <button className="modal-close" type="button" onClick={() => setShowProposal(false)} aria-label="Tutup">×</button>
            {proposalSent ? (
              <div className="proposal-success">
                <span>✓</span>
                <h2>Usulan tersimpan pada mode demo</h2>
                <p>Pada implementasi produksi, formulir dapat diteruskan ke Google Form, Cloudflare Worker, atau proses verifikasi komunitas.</p>
                <button className="button button--primary" type="button" onClick={() => setShowProposal(false)}>Selesai</button>
              </div>
            ) : (
              <form onSubmit={submitProposal}>
                <span className="eyebrow">Kolaborasi warga</span>
                <h2 id="proposal-title">Ajukan kegiatan lingkungan</h2>
                <p>Isi informasi awal agar kegiatan dapat dikurasi sebelum ditampilkan.</p>
                <label>Nama kegiatan<input required name="title" placeholder="Contoh: Clean Walk Taman Kota" /></label>
                <div className="form-grid"><label>Kategori<select name="category"><option>Clean Run</option><option>Penghijauan</option><option>Edukasi</option><option>Mobilitas</option></select></label><label>Tanggal<input required name="date" type="date" /></label></div>
                <label>Lokasi<input required name="location" placeholder="Nama taman atau kawasan" /></label>
                <label>Deskripsi singkat<textarea required name="description" rows={4} placeholder="Jelaskan tujuan, rangkaian kegiatan, dan pihak yang terlibat." /></label>
                <button className="button button--primary button--full" type="submit">Simpan usulan demo</button>
              </form>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
